import Button from "@/common/components/Button";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import { ShowHeader } from "@/common/components/ShowHeader";
import Tabs from "@/common/components/Tabs";
import { tabsDefaultWithFlexGrowClassName } from "@/common/constants/tabsDefaultClassName.constant";
import useCompany from "@/common/hooks/queries/useCompany";
import { useMaintenanceWorkflow } from "@/features/privateCompanies/hooks/maintenance/workflows/useMaintenanceWorkflow";
import { useMaintenanceWorkflowForwardingsDirectiveEntryKlasses } from "@/features/privateCompanies/hooks/maintenance/workflows/useMaintenanceWorkflowForwardingsDirectiveEntryKlasses";
import { ChevronsRightIcon, SignpostIcon } from "lucide-react";
import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MaintenanceWorkflowLoading from "./components/MaintenanceWorkflowLoading";
import MaintenanceWorkflowSidebar from "./components/MaintenanceWorkflowSidebar";
import { getWorkflowTabsList } from "./utils/getWorkflowTabsList";
import { useWorkflowShowStore } from "./stores/workflowShowStore";

const CompanyMaintenanceWorkflowsShow = () => {
  const { companyId, id } = useParams();

  const {
    init,
    setEquipment,
    setCurrentEquipmentUsage,
  } = useWorkflowShowStore();

  const navigate = useNavigate();

  const {
    data: company,
    isPending: companyIsPending,
  } = useCompany({
    id: companyId || '',
  });

  const {
    data: workflow,
    isPending: workflowIsPending,
    error: workflowError = {} as any,
    refetch: refetchWorkflow,
    dataUpdatedAt: workflowUpdatedAt,
  } = useMaintenanceWorkflow(id);

  const {
    data: workflowForwardingsEntryKlasses,
    isPending: workflowForwardingsEntryKlassesIsPending,
  } = useMaintenanceWorkflowForwardingsDirectiveEntryKlasses(id);

  const isScreenLoading = useMemo(() => {
    return workflowIsPending || workflowForwardingsEntryKlassesIsPending;
  }, [workflowIsPending, workflowForwardingsEntryKlassesIsPending]);

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    if (workflow?.equipment) {
      setEquipment(workflow.equipment);
    }

    if (workflow?.equipment_usage_declaration) {
      setCurrentEquipmentUsage(workflow.equipment_usage_declaration.value);
    }
  }, [workflowUpdatedAt]);

  return (
    <div className="inner-screen">
      <ShowHeader
        goBack
        goBackProps={{ fallback: `/companies/${companyId}/maintenance/workflows`, searchLastRouteFromFallback: true }}
        isLoading={companyIsPending || workflowIsPending}
        title={company?.general_information?.fantasy_name}
        subtitle={(
          <div className="flex items-center gap-2">
            <p>
              Processos de manutenção (OS)
            </p>

            <ChevronsRightIcon />

            <p className="text-white-800">
              {workflow?.id.split('-')[0].toUpperCase() || ''}
            </p>
          </div>
        )}
        titleClassName="text-md"
        className="text-white-400"
        subtitleClassName="text-nowrap font-medium"
        buttons={(
          <div>
            {workflow?.status !== 'completed' && (workflow?.current_forwarding === null || workflow?.current_forwarding.status === 'completed') && (
              <Button
                onClick={() => navigate('forwardings/create')}
                operationKlassToCheckPermission="Admin::Maintenance::Forwardings::Create"
              >
                <SignpostIcon size={20} />

                Encaminhar processo
              </Button>
            )}
          </div>
        )}
      />

      <ErrorMessageWrapper
        statusCode={workflowError?.status}
        refetchFunction={refetchWorkflow}
        className="flex-1"
      >
        <div className="flex-1 flex flex-col lg:flex-row overflow-hidden gap-4">
          <MaintenanceWorkflowSidebar workflow={workflow} />

          <div className="flex-1 flex flex-col overflow-auto">
            {isScreenLoading && <MaintenanceWorkflowLoading />}

            {!isScreenLoading && (
              <Tabs
                {...tabsDefaultWithFlexGrowClassName}
                tabsList={getWorkflowTabsList(
                  workflow,
                  workflowForwardingsEntryKlasses,
                )}
              />
            )}
          </div>
        </div>
      </ErrorMessageWrapper>
    </div>
  );
}

export default CompanyMaintenanceWorkflowsShow;
